import type { MailingAddressInput, MailingAddress, Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { ProvinceNameKoMap } from "@repo/utils/src/address";

import type { IOrdersEdgesNode } from "../queries/customer-gql";

// 주소 입력 폼 데이터의 타입 정의
export interface IAddressFormData {
  key: keyof MailingAddressInput;
  name: string;
  required?: boolean;
}

// 각 국가에 대한 타입 정의
interface ICountryData {
  name: string;
  form_order: IAddressFormData[][];
  states?: Record<string, string>;
}

export type TFetchIntlAddressForm = Record<string, ICountryData>;

/**
 * @description country, province 코드 찾기
 */
export const findCodes = ({
  formData,
  country,
  province,
}: {
  formData?: TFetchIntlAddressForm;
  country: string;
  province: string;
}): { countryCode: string; provinceCode: string } => {
  let countryCode = "";
  let provinceCode = "";

  if (!formData) return { countryCode, provinceCode };

  for (const key in formData) {
    if (formData[key].name === country) {
      countryCode = key;
      if (formData[key].states?.[province]) {
        provinceCode = formData[key].states[province];
      }
    }
  }

  return { countryCode, provinceCode };
};

/**
 * @description 주소에 모든 필수 키에 대한 값이 있는지 확인
 */
export const isValidAddress = (address?: MailingAddress): boolean => {
  if (!address) return false;

  const requiredKeys: (keyof MailingAddress)[] = [
    "firstName",
    "lastName",
    "phone",
    // "zip",
    "address1",
    // "address2",
    "province",
  ];

  return requiredKeys.every((key) => Object.prototype.hasOwnProperty.call(address, key) && address[key]);
};

/**
 * @description 국가에 맞는 주소 형태로 변경 + 예외처리
 */
export const formatOrderAddress = ({
  address,
  lang,
}: {
  address?: Maybe<MailingAddress>;
  lang: "ko" | "en" | "jp";
}) => {
  const formatName = (firstName: string, lastName: string): string => {
    return lang === "ko" ? `${lastName}${firstName}` : `${firstName} ${lastName}`;
  };

  const formatCountry = (country: string): string => {
    return lang === "ko" ? "" : country;
  };

  const formatProvince = (province: string): string => {
    if (lang === "ko") return province ? ProvinceNameKoMap[province] : "";
    return province;
  };

  return {
    name: formatName(address?.firstName || "", address?.lastName || ""),
    country: formatCountry(address?.country || ""),
    city: address?.city || "",
    address1: address?.address1 || "",
    address2: address?.address2 || "",
    province: formatProvince(address?.province || ""),
    provinceCode: address?.provinceCode || "",
    firstName: address?.firstName || "",
    lastName: address?.lastName || "",
    phone: String(address?.phone || ""),
    zip: String(address?.zip || ""),
  };
};

export interface IAddresses {
  data: {
    locationGroupZones: {
      edges: {
        zone: {
          id: string;
          name: string;
          countries: {
            id: string;
            name: string;
            code: {
              countryCode: string;
            };
          }[];
        };
        rateDefinitions: {
          edges: {
            node: {
              id: string;
              name: string;
              rateProvider: {
                id: string;
                price: {
                  amount: string;
                  currencyCode: string;
                };
              };
              methodConditions: {
                id: string;
                field: string;
                operator: string;
                conditionCriteria: {
                  unit: string;
                  value: number;
                };
              }[];
            };
          }[];
        };
      }[];
    };
  };
}

/**
 * @description intl 주소에 따른 배송 업체 및 금액 찾기
 */
export const getShippingInfo = ({
  order,
  weightSum,
  country,
  lang,
  addresses,
  defaultWeight,
}: {
  order?: IOrdersEdgesNode;
  weightSum?: number;
  country?: string;
  lang: "ko" | "en" | "jp";
  addresses: IAddresses;
  defaultWeight: number;
}) => {
  if (lang === "ko") {
    return {
      shippingMethod: "CJ 대한통운",
      shippingFee: 0,
    };
  }

  const UNIT_TO_KG = {
    GRAMS: 1000,
    KILOGRAMS: 1,
    OUNCES: 35.274,
    POUNDS: 2.205,
  };

  const shippingCountry = country ?? (order?.shippingAddress?.country || "");

  const calcWeight =
    order?.lineItems.edges.reduce((sum, { node }) => {
      const unit = node.variant?.weightUnit || "KILOGRAMS";
      const calcUnit = UNIT_TO_KG[unit];
      return sum + (parseFloat(String(node.variant?.weight ?? 1)) / calcUnit) * node.quantity;
    }, 0) || 0;

  const orderWeight = Math.ceil((calcWeight + defaultWeight) * 10) / 10;

  const totalWeight = weightSum ?? orderWeight;

  // 배송지 국가에 따른 배송 정보 가져오기
  const locationGroupZone = addresses.data.locationGroupZones.edges.find(({ zone }) =>
    zone.countries.some(({ name }) => name === shippingCountry),
  );

  // 가장 비용이 큰 zone
  const biggestRate = locationGroupZone?.rateDefinitions.edges[locationGroupZone.rateDefinitions.edges.length - 1];

  const maxCondition = biggestRate?.node.methodConditions.find((item) => item.operator.includes("GREATER"));

  // 배송 무게가 최대치인지
  const isOverMaxWeight = (maxCondition?.conditionCriteria.value ?? 0) < totalWeight;

  // 배송 무게에 맞는 구간
  const matchedZone = locationGroupZone?.rateDefinitions.edges.find(({ node }) => {
    const min = node.methodConditions.find((item) => item.operator.includes("GREATER"))?.conditionCriteria.value ?? 0;
    const max = node.methodConditions.find((item) => item.operator.includes("LESS"))?.conditionCriteria.value ?? 0;
    return min <= totalWeight && totalWeight <= max;
  });

  // 배송 구간 찾기
  const shippingInfo = isOverMaxWeight ? biggestRate : matchedZone;

  const shippingZone = locationGroupZone?.zone.countries.find((item) => item.name === shippingCountry);

  const shippingTitle = order?.customAttributes.find((item) => item.key === "shippingTitle");

  const shippingMethod = shippingTitle ? shippingTitle.value : shippingInfo?.node.name || "Shipping";
  const shippingFee = parseFloat(shippingInfo?.node.rateProvider.price.amount || "0");

  return { shippingZone, shippingMethod, shippingFee };
};
